/**
 * DictationInput — controlled input for melodic-dictation exercises.
 *
 * Two input modes the user can switch between:
 *
 *   - "solfege": a row of clickable syllables (do/re/mi/...). Octave-
 *     agnostic — the entered ScaleDegree carries no `octaveOffset`.
 *     The chromatic row (di/ri/fi/le/te) is shown only when
 *     `chromaticEnabled` is true.
 *
 *   - "piano": the existing <PianoKeyboard>. Click a key, the entered
 *     ScaleDegree carries `degree`, optional `alteration` (chromatic
 *     notes), and `octaveOffset` from the reference octave the
 *     generator used (tonicMidi + 12).
 *
 * Controlled — the parent owns `sequence` so it can pre-populate from
 * a resume hint, clear on next-question, or grade and pass per-note
 * statuses back via `perNote`.
 *
 * MIDI integration: the parent passes an `onMidiNoteOn` registration
 * function. When provided, this component subscribes on mount and
 * appends incoming noteons through the same code path as a piano click.
 * The component never imports Web MIDI directly — that boundary stays
 * inside the apps/web Practice route.
 */

import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  type ScaleDegree,
  type ScaleDegreeSequence,
  midiToScaleDegree,
} from "@music-app/theory-engine";
import { PianoKeyboard } from "./PianoKeyboard.js";

export type DictationNoteStatus =
  | "correct"
  | "wrong-pitch"
  | "wrong-octave"
  | "missing"
  | "extra";

export type DictationInputMode = "solfege" | "piano";

export interface DictationInputProps {
  /** Pick the solfège row: "major" (do re mi…) vs "minor" (do re me…). */
  tonalityMode: "major" | "minor";
  /** MIDI of the established tonic. Anchors piano-mode entry. */
  tonicMidi: number;
  /** Generator's range hint — sets the piano keyboard's default span. */
  range: "narrow" | "wide";
  /** Whether to show the chromatic syllable row in solfège mode. */
  chromaticEnabled?: boolean;
  /** Current entered sequence (controlled). */
  sequence: ScaleDegreeSequence;
  onChange: (next: ScaleDegreeSequence) => void;
  /** After grading, the parent passes per-note statuses to color chips. */
  perNote?: ReadonlyArray<DictationNoteStatus>;
  /** Disables the input grid (during playback, after submit). */
  disabled?: boolean;
  /** Default "solfege". */
  initialMode?: DictationInputMode;
  /**
   * Optional MIDI noteon stream. Pass a function that registers a
   * handler and returns an unsubscribe; the component subscribes on
   * mount. When omitted, MIDI input is simply not active. Practice
   * supplies this when `prefs.midiInputEnabled` is on.
   */
  onMidiNoteOn?: (handler: (midi: number) => void) => () => void;
  /** Optional target length to display in the counter (e.g. "3 / 5 notes"). */
  expectedLength?: number;
}

const MAJOR_SOLFEGE = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;
const MINOR_SOLFEGE = ["la", "ti", "do", "re", "mi", "fa", "sol"] as const;

interface ChromaticEntry {
  syllable: string;
  degree: number;
  alteration: "♯" | "♭";
}

/**
 * Common chromatic syllables in major-mode solfège pedagogy. Mirrors
 * the chromatic-degrees-major curriculum node's selection (♯1, ♯2, ♯4,
 * ♭6, ♭7) — the historically musical chromatic neighbors.
 */
const CHROMATIC_ROW_MAJOR: ReadonlyArray<ChromaticEntry> = [
  { syllable: "di", degree: 1, alteration: "♯" },
  { syllable: "ri", degree: 2, alteration: "♯" },
  { syllable: "fi", degree: 4, alteration: "♯" },
  { syllable: "le", degree: 6, alteration: "♭" },
  { syllable: "te", degree: 7, alteration: "♭" },
];

export function DictationInput({
  tonalityMode,
  tonicMidi,
  range,
  chromaticEnabled = false,
  sequence,
  onChange,
  perNote,
  disabled = false,
  initialMode = "solfege",
  onMidiNoteOn,
  expectedLength,
}: DictationInputProps): ReactNode {
  const [mode, setMode] = useState<DictationInputMode>(initialMode);

  const referenceMidi = tonicMidi + 12;

  // MIDI subscription. Piano-mode behavior is shared with click — both
  // run through `appendFromMidi`. We subscribe regardless of mode
  // because the user might play a MIDI note while in solfège mode and
  // expect it to land. The handler reads the *latest* sequence + onChange
  // closure via refs to avoid re-subscribing every render.
  useEffect(() => {
    if (!onMidiNoteOn) return;
    if (disabled) return;
    return onMidiNoteOn((midi) => {
      const sd = midiToScaleDegree(midi, tonicMidi, tonalityMode, {
        allowChromatic: true,
        referenceTonicMidi: referenceMidi,
      });
      if (sd === null) return;
      onChange([...sequence, sd]);
    });
    // We intentionally re-subscribe whenever `sequence` changes —
    // otherwise the handler captures a stale array. The subscription
    // is cheap (no I/O), so the per-keystroke re-bind is fine.
  }, [
    onMidiNoteOn,
    disabled,
    tonicMidi,
    tonalityMode,
    referenceMidi,
    sequence,
    onChange,
  ]);

  const solfegeRow = useMemo(
    () => (tonalityMode === "major" ? MAJOR_SOLFEGE : MINOR_SOLFEGE),
    [tonalityMode],
  );

  const handleSolfegeClick = (i: number) => {
    if (disabled) return;
    onChange([...sequence, { degree: i + 1 }]);
  };

  const handleChromaticClick = (entry: ChromaticEntry) => {
    if (disabled) return;
    onChange([...sequence, { degree: entry.degree, alteration: entry.alteration }]);
  };

  const handlePianoClick = (midi: number) => {
    if (disabled) return;
    const sd = midiToScaleDegree(midi, tonicMidi, tonalityMode, {
      allowChromatic: true,
      referenceTonicMidi: referenceMidi,
    });
    if (sd === null) return;
    onChange([...sequence, sd]);
  };

  const handleUndo = () => {
    if (disabled) return;
    if (sequence.length === 0) return;
    onChange(sequence.slice(0, -1));
  };

  const handleClear = () => {
    if (disabled) return;
    if (sequence.length === 0) return;
    onChange([]);
  };

  const counterText = (() => {
    if (expectedLength === undefined) {
      return `${sequence.length} note${sequence.length === 1 ? "" : "s"}`;
    }
    return `${sequence.length} / ${expectedLength} notes entered`;
  })();

  return (
    <div className="space-y-3" data-testid="dictation-input">
      {/* Entered chips */}
      <div
        className="min-h-[44px] p-2 border border-ink/15 bg-paper flex flex-wrap items-center gap-1"
        role="list"
        aria-label="Entered notes"
        data-testid="entered-chips"
      >
        {sequence.length === 0 ? (
          <span className="text-xs text-inkFaded font-mono italic">
            no notes entered
          </span>
        ) : (
          sequence.map((sd, i) => {
            const status = perNote?.[i] ?? null;
            return (
              <span
                key={`${i}-${sd.degree}-${sd.alteration ?? ""}`}
                role="listitem"
                aria-label={chipAriaLabel(sd, status, i + 1)}
                className={chipClass(status)}
              >
                {chipLabel(sd, tonalityMode)}
              </span>
            );
          })
        )}
      </div>

      {/* Mode toggle + counter + actions */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div role="group" aria-label="Input mode" className="flex gap-1">
          <button
            type="button"
            aria-pressed={mode === "solfege"}
            onClick={() => setMode("solfege")}
            disabled={disabled}
            className={modeButtonClass(mode === "solfege")}
          >
            Solfège
          </button>
          <button
            type="button"
            aria-pressed={mode === "piano"}
            onClick={() => setMode("piano")}
            disabled={disabled}
            className={modeButtonClass(mode === "piano")}
          >
            Piano
          </button>
        </div>

        <span
          className="text-xs font-mono text-inkSoft"
          aria-live="polite"
          data-testid="counter"
        >
          {counterText}
        </span>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={handleUndo}
            disabled={disabled || sequence.length === 0}
            className="py-1 px-2 text-xs border border-ink/20 hover:bg-ink hover:text-paper transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled || sequence.length === 0}
            className="py-1 px-2 text-xs border border-ink/20 hover:bg-ink hover:text-paper transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Input surface */}
      {mode === "solfege" ? (
        <div className="space-y-2">
          <div className="grid grid-cols-7 gap-2">
            {solfegeRow.map((syl, i) => (
              <button
                key={syl}
                type="button"
                disabled={disabled}
                onClick={() => handleSolfegeClick(i)}
                aria-label={`Append ${syl}`}
                className="py-3 px-1 border border-ink/20 bg-paper hover:bg-ink hover:text-paper hover:border-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="block font-serif italic text-lg font-medium">{syl}</span>
              </button>
            ))}
          </div>
          {chromaticEnabled && (
            <div
              className="grid grid-cols-5 gap-2"
              role="group"
              aria-label="Chromatic syllables"
            >
              {CHROMATIC_ROW_MAJOR.map((entry) => (
                <button
                  key={entry.syllable}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleChromaticClick(entry)}
                  aria-label={`Append chromatic ${entry.syllable}`}
                  className="py-2 px-1 border border-ink/20 bg-paperWarm hover:bg-ink hover:text-paper hover:border-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                >
                  <span className="block font-serif italic font-medium">{entry.syllable}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <PianoKeyboard
          fromMidi={pianoFromMidi(tonicMidi, range)}
          toMidi={pianoToMidi(tonicMidi, range)}
          onKeyClick={handlePianoClick}
        />
      )}
    </div>
  );
}

function pianoFromMidi(tonicMidi: number, range: "narrow" | "wide"): number {
  // Anchor the keyboard a comfortable bit below the reference octave so
  // the user can play notes in the heard register and one octave below.
  return Math.max(36, tonicMidi - (range === "narrow" ? 0 : 12));
}

function pianoToMidi(tonicMidi: number, range: "narrow" | "wide"): number {
  // Reference octave + one above — wide range goes higher.
  return Math.min(96, tonicMidi + 12 + (range === "narrow" ? 12 : 24));
}

function chipLabel(sd: ScaleDegree, tonalityMode: "major" | "minor"): string {
  const row = tonalityMode === "major" ? MAJOR_SOLFEGE : MINOR_SOLFEGE;
  const baseSyllable = row[((sd.degree - 1) % 7 + 7) % 7]!;
  if (!sd.alteration) return baseSyllable;
  // Render the chromatic syllable when applicable, else fall back to
  // "syllable + alteration" (degrees 3, 4 with ♭ have no standard
  // chromatic syllable; "fa♭" is acceptable).
  if (sd.alteration === "♯") {
    const chromatic = CHROMATIC_ROW_MAJOR.find(
      (c) => c.degree === sd.degree && c.alteration === "♯",
    );
    if (chromatic) return chromatic.syllable;
  }
  if (sd.alteration === "♭") {
    const chromatic = CHROMATIC_ROW_MAJOR.find(
      (c) => c.degree === sd.degree && c.alteration === "♭",
    );
    if (chromatic) return chromatic.syllable;
  }
  return `${baseSyllable}${sd.alteration}`;
}

function chipAriaLabel(
  sd: ScaleDegree,
  status: DictationNoteStatus | null,
  position: number,
): string {
  const label = `position ${position}: degree ${sd.degree}${sd.alteration ?? ""}`;
  if (!status) return label;
  if (status === "correct") return `${label} (correct)`;
  if (status === "wrong-pitch") return `${label} (wrong pitch)`;
  if (status === "wrong-octave") return `${label} (wrong octave)`;
  if (status === "extra") return `${label} (extra note)`;
  return label;
}

function chipClass(status: DictationNoteStatus | null): string {
  const base =
    "inline-block px-2 py-1 text-sm font-serif italic border min-w-[2.5rem] text-center";
  if (!status) return `${base} bg-paperWarm border-ink/20 text-ink`;
  if (status === "correct") return `${base} bg-accent text-paper border-accent`;
  if (status === "wrong-pitch") return `${base} bg-ink text-paper border-ink line-through`;
  if (status === "wrong-octave")
    return `${base} bg-warning/30 border-warning text-ink`;
  return `${base} bg-paperWarm border-ink/40 text-inkFaded line-through`;
}

function modeButtonClass(active: boolean): string {
  const base = "py-1.5 px-3 text-xs border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
  return active
    ? `${base} bg-ink text-paper border-ink`
    : `${base} bg-paper text-ink border-ink/20 hover:bg-paperWarm`;
}
